import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import "../../assets/css/onboarding.css";
import AppButton from "../layout/AppButton";
import OnboardingChat from "./OnboardingChat";
import { useOnboarding } from "../../hooks/useOnboarding";
import { apiService } from "../../services/apiClient";
import { apiUrl } from "../../services/apiUrl";

let _tmpId = -1;
const tmpId = () => _tmpId--;

const normQuestion = (q, scope) => ({
  id: q.id,
  scope: q.scope ?? scope ?? "dashboard",
  question: q.question ?? q.question_text ?? "",
  dax: q.dax ?? q.dax_query ?? "",
  table: q.table ?? q.source_table ?? null,
  verified: q.verified ?? false,
  verifyStatus: q.verifyStatus ?? q.verify_status ?? "unverified",
  verifyError: q.verifyError ?? q.verify_error ?? null,
  sample: q.sample ?? [],
  selected: q.selected ?? q.verified ?? false,
  rating: q.rating ?? 0,
  feedback: q.feedback ?? "",
});
const normKpi = (k) => ({
  id: k.id, name: k.name ?? k.kpi_name ?? "", definition: k.definition ?? "",
  dax: k.dax ?? k.dax_or_sql ?? "", table: k.table ?? k.source_table ?? null,
  selected: k.selected ?? true, rating: k.rating ?? 0, feedback: k.feedback ?? "",
});
const normMeta = (m) => ({
  id: m.id, objectType: m.objectType ?? m.object_type ?? "term",
  objectName: m.objectName ?? m.object_name ?? "",
  description: m.description ?? "", synonyms: m.synonyms ?? "",
  selected: m.selected ?? true, feedback: m.feedback ?? "",
});
const normMeasure = (m) => {
  const dax = m.dax_expression ?? m.dax ?? "";
  const verifyStatus = m.verifyStatus ?? m.verify_status ?? "unverified";
  const hasDax = !!dax.trim();
  const isVerified = verifyStatus === "ok" || verifyStatus === "valid";
  return {
    id: m.id, name: m.measure_name ?? m.name ?? "", table: m.table_name ?? m.table ?? "",
    dax, description: m.description ?? "",
    confirmed: m.confirmed != null ? m.confirmed : (isVerified || !hasDax),
    feedback: m.feedback ?? "",
    verifyStatus, verifyError: m.verifyError ?? null,
  };
};

const SampleTable = ({ rows }) => {
  if (!rows?.length) return null;
  const cols = Object.keys(rows[0]);
  return (
    <div className="ob-sample">
      <table>
        <thead><tr>{cols.map((c) => <th key={c}>{c.replace(/[[\]]/g, "")}</th>)}</tr></thead>
        <tbody>{rows.slice(0, 5).map((r, i) => <tr key={i}>{cols.map((c) => <td key={c}>{String(r[c])}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
};

const VerifyBadge = ({ status }) => {
  if (status === "ok") return <span className="ob-badge ok">✓ Verified</span>;
  if (status === "failed") return <span className="ob-badge fail">✗ DAX failed</span>;
  return <span className="ob-badge unver">Unverified</span>;
};

const PanelStatus = ({ status }) => {
  if (status === "loading") return <span className="ob-panel-status loading"><span className="ob-spin" /> Loading…</span>;
  if (status === "ready") return <span className="ob-panel-status ready">Ready</span>;
  if (status === "edited") return <span className="ob-panel-status edited">Edited</span>;
  if (status === "error") return <span className="ob-panel-status error">Error</span>;
  return null;
};

const DashboardWithChat = ({ reportGuid, reportId, reportName, suggestedQuestions = [] }) => {
  const [embedConfig, setEmbedConfig] = useState(null);
  const [embedError, setEmbedError] = useState(null);

  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiService.get(apiUrl.aipdReportData(reportId));
        const data = resp?.data?.data ?? resp?.data;
        const embed = data?.embedReport;
        if (!cancelled && embed?.embedToken && embed?.url) {
          setEmbedConfig({ token: embed.embedToken, url: embed.url, id: embed.reportGuid || reportGuid });
        } else if (!cancelled) {
          setEmbedError("Could not load dashboard embed config");
        }
      } catch {
        if (!cancelled) setEmbedError("Failed to load dashboard preview");
      }
    })();
    return () => { cancelled = true; };
  }, [reportId, reportGuid]);

  return (
    <div className="ob-dashboard-chat">
      <div className="ob-dashboard-pane">
        {embedError && <div className="ob-alert warn">{embedError}</div>}
        {!embedConfig && !embedError && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}><span className="ob-spin" /> Loading dashboard…</div>}
        {embedConfig && (
          <PowerBIEmbed
            embedConfig={{
              type: "report",
              id: embedConfig.id,
              embedUrl: embedConfig.url,
              accessToken: embedConfig.token,
              tokenType: models.TokenType.Embed,
              permissions: models.Permissions.Read,
              viewMode: models.ViewMode.View,
              settings: {
                navContentPaneEnabled: true,
                filterPaneEnabled: false,
                bars: { actionBar: { visible: false }, statusBar: { visible: false } },
              },
            }}
            cssClassName="ob-pbi-embed"
          />
        )}
      </div>
      <div className="ob-chat-pane">
        <OnboardingChat reportGuid={reportGuid} reportName={reportName} suggestedQuestions={suggestedQuestions} />
      </div>
    </div>
  );
};

const OnboardingWizard = ({ reports = [], onComplete }) => {
  const ob = useOnboarding();
  const navigate = useNavigate();

  // Phase: "connect" | "tables" | "accordion" | "done"
  const [phase, setPhase] = useState("connect");
  const [reportGuid, setReportGuid] = useState("");
  const [onboardingId, setOnboardingId] = useState(null);
  const [tables, setTables] = useState({});
  const [selectedTables, setSelectedTables] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [dashQ, setDashQ] = useState([]);
  const [srcQ, setSrcQ] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [showDax, setShowDax] = useState({});
  const [editDax, setEditDax] = useState({});
  const [genDaxBusy, setGenDaxBusy] = useState({});
  const [genMeasureBusy, setGenMeasureBusy] = useState({});
  const [genAllMeasuresBusy, setGenAllMeasuresBusy] = useState(false);
  const [verifyMeasureBusy, setVerifyMeasureBusy] = useState({});
  const [notice, setNotice] = useState(null);
  const [domainContext, setDomainContext] = useState("");
  const [genContextBusy, setGenContextBusy] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState(new Set());
  const [sectionStatus, setSectionStatus] = useState({});
  const [existingOnboarding, setExistingOnboarding] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const selectedReport = useMemo(
    () => reports.find((r) => r.reportId === reportGuid),
    [reports, reportGuid]
  );

  const setItem = (setter, id, patch) =>
    setter((list) => list.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const removeItem = (setter, id) =>
    setter((list) => list.filter((it) => it.id !== id));

  // ── Connect phase ──────────────────────────────────────────────────────────
  const handleStart = async () => {
    if (!reportGuid) return;
    setNotice(null);
    setExistingOnboarding(null);

    // Check for existing onboarding first
    try {
      const statusData = await ob.getStatusByGuid(reportGuid);
      const existingOb = statusData?.onboarding;
      const oid = existingOb?.onboarding_id || existingOb?.id;
      if (oid && existingOb.status === "completed") {
        setExistingOnboarding({ onboarding_id: oid, status: existingOb.status, ...existingOb });
        return;
      }
      if (oid && existingOb.status === "in_progress") {
        setExistingOnboarding({ onboarding_id: oid, status: existingOb.status, ...existingOb });
        return;
      }
    } catch { /* no existing — proceed to create */ }

    try {
      const data = await ob.start(reportGuid);
      setOnboardingId(data.onboardingId);
      setTables(data.tables || {});
      setMeasures((data.measures || []).map((m, i) => normMeasure({ ...m, id: m.id ?? `tmp-${i}` })));
      if (data.schemaSource === "mock") setNotice("Running in MOCK mode — sample schema, no live Power BI calls.");
      if (data.schemaSource === "unavailable") setNotice(data.message || "INFO.VIEW introspection unavailable.");
      setPhase("tables");
    } catch { /* error already shown via ob.error */ }
  };

  const loadExistingState = async (id) => {
    const state = await ob.getState(id);
    // Schema/tables from the onboarding record
    if (state?.onboarding?.schema_json) {
      try {
        const schema = typeof state.onboarding.schema_json === "string"
          ? JSON.parse(state.onboarding.schema_json)
          : state.onboarding.schema_json;
        setTables(schema.tables || schema);
      } catch {}
    }
    if (state?.onboarding?.selected_tables_json) {
      try { setSelectedTables(JSON.parse(state.onboarding.selected_tables_json)); } catch {}
    }
    if (state?.measures?.length) setMeasures(state.measures.map(normMeasure));
    // Questions come as separate arrays from backend
    const dQuestions = state?.dashboardQuestions || state?.questions?.filter(q => q.scope === "dashboard") || [];
    const sQuestions = state?.sourceQuestions || state?.questions?.filter(q => q.scope === "source_table") || [];
    if (dQuestions.length) setDashQ(dQuestions.map(q => normQuestion(q, "dashboard")));
    if (sQuestions.length) setSrcQ(sQuestions.map(q => normQuestion(q, "source_table")));
    if (state?.kpis?.length) setKpis(state.kpis.map(normKpi));
    if (state?.metadata?.length) setMetadata(state.metadata.map(normMeta));
    if (state?.onboarding?.domain_context) setDomainContext(state.onboarding.domain_context);
    setSectionStatus({
      measures: state?.measures?.length ? "ready" : "loading",
      questions: (state?.questions?.length) ? "ready" : "loading",
      kpis: state?.kpis?.length ? "ready" : "loading",
      metadata: state?.metadata?.length ? "ready" : "loading",
      context: state?.onboarding?.domain_context ? "ready" : "loading",
      review: "ready",
    });
    setExpandedPanels(new Set());
    setPhase("accordion");
  };

  const handleEdit = async () => {
    if (!existingOnboarding?.onboarding_id) return;
    await ob.reopenOnboarding(existingOnboarding.onboarding_id);
    setOnboardingId(existingOnboarding.onboarding_id);
    setExistingOnboarding(null);
    await loadExistingState(existingOnboarding.onboarding_id);
  };

  const handleDelete = async () => {
    if (!existingOnboarding?.onboarding_id) return;
    await ob.deleteOnboarding(existingOnboarding.onboarding_id);
    setExistingOnboarding(null);
    setShowDeleteConfirm(false);
    setNotice("Onboarding deleted. The chatbot is now disabled for this dashboard.");
  };

  // ── Tables phase ───────────────────────────────────────────────────────────
  const toggleTable = (t) =>
    setSelectedTables((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);

  const saveTablesAndPrepopulate = async () => {
    await ob.saveTables(onboardingId, selectedTables);
    setSectionStatus({ measures: "loading", questions: "loading", kpis: "loading", metadata: "loading", context: "loading", review: "ready" });
    setExpandedPanels(new Set());
    setPhase("accordion");

    try {
      const result = await ob.prepopulate(onboardingId);
      const sections = result?.sections || {};
      if (sections.dashboard_questions?.data?.questions) {
        setDashQ(sections.dashboard_questions.data.questions.map(q => normQuestion(q, "dashboard")));
      }
      if (sections.source_questions?.data?.questions) {
        setSrcQ(sections.source_questions.data.questions.map(q => normQuestion(q, "source_table")));
      }
      if (sections.kpis?.data?.kpis) {
        setKpis(sections.kpis.data.kpis.map(normKpi));
      }
      if (sections.metadata?.data?.metadata) {
        setMetadata(sections.metadata.data.metadata.map(normMeta));
      }
      if (sections.domain_context?.data?.context) {
        setDomainContext(sections.domain_context.data.context);
      }
      if (sections.suggested_measures?.data?.measures?.length) {
        setMeasures(sections.suggested_measures.data.measures.map((m, i) =>
          normMeasure({ ...m, id: m.id ?? `ai-${i}`, measure_name: m.name, table_name: m.table })
        ));
      }
      setSectionStatus({
        measures: "ready",
        questions: (sections.dashboard_questions?.status === 200 || sections.source_questions?.status === 200) ? "ready" : "error",
        kpis: sections.kpis?.status === 200 ? "ready" : "error",
        metadata: sections.metadata?.status === 200 ? "ready" : "error",
        context: sections.domain_context?.status === 200 ? "ready" : "error",
        review: "ready",
      });
    } catch {
      setSectionStatus({ measures: "error", questions: "error", kpis: "error", metadata: "error", context: "error", review: "ready" });
    }
  };

  // ── Auto-generate DAX for measures missing it ──
  const autoGenRef = useRef(false);
  useEffect(() => {
    if (phase !== "accordion" || !onboardingId || autoGenRef.current) return;
    const emptyDax = measures.filter((m) => m.name.trim() && !m.dax.trim());
    if (!emptyDax.length) return;
    autoGenRef.current = true;
    handleGenAllMeasuresDax();
  }, [phase, measures, onboardingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-validate measures that have DAX but are unverified ──
  const autoValidatedRef = useRef(new Set());
  useEffect(() => {
    if (phase !== "accordion" || !onboardingId) return;
    const toValidate = measures.filter(
      (m) => m.dax.trim() && m.verifyStatus === "unverified" && !autoValidatedRef.current.has(m.id)
    );
    if (!toValidate.length) return;
    for (const m of toValidate) {
      autoValidatedRef.current.add(m.id);
      handleVerifyMeasure(m);
    }
  }, [phase, measures, onboardingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Select / deselect all ──────────────────────────────────────────────────
  const selectAll = (setter) => setter((list) => list.map((it) => ({ ...it, selected: true })));
  const deselectAll = (setter) => setter((list) => list.map((it) => ({ ...it, selected: false })));

  const SelectAllBar = ({ list, setter }) => {
    const allOn = list.length > 0 && list.every((it) => it.selected);
    return (
      <div className="ob-select-all-bar">
        <button className="ob-toggle-link" onClick={() => allOn ? deselectAll(setter) : selectAll(setter)}>
          {allOn ? "Deselect all" : "Select all"}
        </button>
        <span className="ob-sel-count">{list.filter((it) => it.selected).length} / {list.length} selected</span>
      </div>
    );
  };

  // ── Add blank items ────────────────────────────────────────────────────────
  const addQuestion = (setter, scope) =>
    setter((list) => [...list, normQuestion({ id: tmpId(), scope, question: "", dax: "", selected: true }, scope)]);

  const addKpi = () =>
    setKpis((list) => [...list, normKpi({ id: tmpId(), name: "", definition: "", dax: "" })]);

  const addMeta = () =>
    setMetadata((list) => [...list, normMeta({ id: tmpId(), objectType: "term", objectName: "", description: "", synonyms: "" })]);

  const addMeasure = () =>
    setMeasures((list) => [...list, normMeasure({ id: tmpId(), name: "", table: "", dax: "", description: "" })]);

  // ── Measure handlers ───────────────────────────────────────────────────────
  const handleVerifyMeasure = async (m) => {
    if (!m.dax.trim()) return;
    setVerifyMeasureBusy((s) => ({ ...s, [m.id]: true }));
    try {
      const res = await ob.verifyMeasure(onboardingId, m.name, m.dax);
      const data = res?.data ?? res;
      const status = data?.verify_status ?? data?.verifyStatus ?? "unverified";
      const isOk = status === "ok" || status === "valid";
      setItem(setMeasures, m.id, {
        verifyStatus: status,
        verifyError: data?.verify_error ?? data?.verifyError ?? null,
        ...(isOk ? { confirmed: true } : {}),
      });
    } catch {
      setItem(setMeasures, m.id, { verifyStatus: "failed", verifyError: "Request failed" });
    } finally {
      setVerifyMeasureBusy((s) => ({ ...s, [m.id]: false }));
    }
  };

  const handleGenMeasureDax = async (m) => {
    if (!m.name.trim()) return;
    setGenMeasureBusy((s) => ({ ...s, [m.id]: true }));
    try {
      const hasFailed = m.verifyStatus === "failed" || m.verifyStatus === "error" || m.verifyError;
      const prevDax = hasFailed ? m.dax : undefined;
      const prevError = hasFailed ? m.verifyError : undefined;
      const result = await ob.generateMeasureDax(onboardingId, m.name, prevDax, prevError);
      if (result?.dax) {
        const status = result.verifyStatus ?? result.verify_status ?? "unverified";
        const isOk = status === "ok" || status === "valid";
        setItem(setMeasures, m.id, {
          dax: result.dax,
          verifyStatus: status,
          verifyError: result.verifyError ?? result.verify_error ?? null,
          confirmed: true,
        });
      }
    } catch { /* shown via ob.error */ }
    finally { setGenMeasureBusy((s) => ({ ...s, [m.id]: false })); }
  };

  const handleGenAllMeasuresDax = async () => {
    const empty = measures.filter((m) => !m.dax.trim() && m.name.trim());
    if (!empty.length) return;
    setGenAllMeasuresBusy(true);
    try {
      const result = await ob.generateMeasuresDaxBulk(onboardingId, empty.map((m) => m.name));
      if (result?.measures) {
        const toVerify = [];
        setMeasures((list) => list.map((m) => {
          const dax = result.measures[m.name];
          if (dax) {
            toVerify.push({ id: m.id, name: m.name, dax });
            return { ...m, dax, verifyStatus: "unverified", verifyError: null, confirmed: true };
          }
          return m;
        }));
        // Verify each with the actual generated DAX
        for (const m of toVerify) handleVerifyMeasure(m);
      }
    } catch { /* shown via ob.error */ }
    finally { setGenAllMeasuresBusy(false); }
  };

  // ── Question DAX generation ────────────────────────────────────────────────
  const handleGenDax = async (setter, q) => {
    if (!q.question.trim()) return;
    setGenDaxBusy((s) => ({ ...s, [q.id]: true }));
    try {
      const hasFailed = q.verifyStatus === "failed" || q.verifyStatus === "error" || q.verifyError;
      const prevDax = hasFailed ? q.dax : undefined;
      const prevError = hasFailed ? q.verifyError : undefined;
      const result = await ob.generateQuestionDax(onboardingId, q.question, q.scope, prevDax, prevError);
      if (result?.dax) {
        setItem(setter, q.id, {
          dax: result.dax,
          verifyStatus: result.verifyStatus ?? "unverified",
          verifyError: result.verifyError ?? null,
          sample: result.sample ?? [],
        });
        setEditDax((s) => ({ ...s, [q.id]: result.dax }));
        setShowDax((s) => ({ ...s, [q.id]: true }));
      }
    } catch { /* error already shown via ob.error */ }
    finally {
      setGenDaxBusy((s) => ({ ...s, [q.id]: false }));
    }
  };

  // ── Regenerate individual sections ─────────────────────────────────────────
  const regenerateDashQ = async () => {
    const data = await ob.genDashboardQuestions(onboardingId);
    setDashQ((data.questions || []).map((q) => normQuestion(q, "dashboard")));
  };
  const regenerateSrcQ = async () => {
    const data = await ob.genSourceQuestions(onboardingId);
    setSrcQ((data.questions || []).map((q) => normQuestion(q, "source_table")));
  };
  const regenerateKpis = async () => {
    const data = await ob.genKpis(onboardingId);
    setKpis((data.kpis || []).map(normKpi));
  };
  const regenerateMeta = async () => {
    const data = await ob.genMetadata(onboardingId);
    setMetadata((data.metadata || []).map(normMeta));
  };
  const handleGenDomainContext = async () => {
    setGenContextBusy(true);
    try {
      const result = await ob.genDomainContext(onboardingId);
      if (result?.context) setDomainContext(result.context);
    } catch { /* shown via ob.error */ }
    finally { setGenContextBusy(false); }
  };

  // ── Save all & finalize ────────────────────────────────────────────────────
  const saveAllSections = async () => {
    const allQ = [
      ...dashQ.map((q) => ({ id: typeof q.id === "number" ? q.id : null, scope: "dashboard", table: q.table, question: q.question, dax: q.dax, selected: q.selected, rating: q.rating, feedback: q.feedback })),
      ...srcQ.map((q) => ({ id: typeof q.id === "number" ? q.id : null, scope: "source_table", table: q.table, question: q.question, dax: q.dax, selected: q.selected, rating: q.rating, feedback: q.feedback })),
    ];
    await ob.saveFeedback(onboardingId, {
      measures: measures.map((m) => ({
        id: typeof m.id === "number" ? m.id : null,
        name: m.name, table: m.table, dax: m.dax,
        confirmed: m.confirmed, description: m.description, feedback: m.feedback,
      })),
      questions: allQ,
      kpis: kpis.map((k) => ({
        id: typeof k.id === "number" ? k.id : null,
        name: k.name, definition: k.definition, dax: k.dax, table: k.table,
        selected: k.selected, rating: k.rating, feedback: k.feedback,
      })),
      metadata: metadata.map((d) => ({
        id: typeof d.id === "number" ? d.id : null,
        objectType: d.objectType, objectName: d.objectName,
        description: d.description, synonyms: d.synonyms,
        selected: d.selected, feedback: d.feedback,
      })),
    });
    if (domainContext.trim()) {
      await ob.saveDomainContext(onboardingId, domainContext);
    }
  };

  const doFinalize = async () => {
    await saveAllSections();
    await ob.finalize(onboardingId);
    if (onComplete) onComplete(selectedReport);
    setPhase("done");
  };

  // ── Question renderer ──────────────────────────────────────────────────────
  const renderQuestions = (list, setter, scope) => (
    <div className="ob-items">
      <SelectAllBar list={list} setter={setter} />
      {!list.length && sectionStatus.questions === "loading" && (
        <div className="ob-empty"><span className="ob-spin" /> Generating questions…</div>
      )}
      {!list.length && sectionStatus.questions !== "loading" && (
        <div className="ob-empty">No questions yet. Add one below or click Regenerate.</div>
      )}
      {list.map((q) => (
        <div key={q.id} className={`ob-item ${q.selected ? "sel" : ""}`}>
          <div className="ob-item-head">
            <input type="checkbox" checked={q.selected} onChange={(e) => setItem(setter, q.id, { selected: e.target.checked })} />
            <textarea
              className="ob-textarea ob-q-edit"
              rows={2}
              value={q.question}
              placeholder="Question text…"
              onChange={(e) => setItem(setter, q.id, { question: e.target.value })}
              style={{ flex: 1, marginLeft: 8 }}
            />
            <VerifyBadge status={q.verifyStatus} />
            {q.scope && <span className="ob-scope-tag">{q.scope === "dashboard" ? "Dashboard" : "Model"}</span>}
            <button className="ob-icon-btn ob-del" title="Remove" onClick={() => removeItem(setter, q.id)}>✕</button>
          </div>
          {q.table && <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>Table: {q.table}</div>}
          {q.verifyError && <div className="ob-verr">{q.verifyError}</div>}
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6 }}>
            <button className="ob-toggle-link" onClick={() => setShowDax((s) => ({ ...s, [q.id]: !s[q.id] }))}>
              {showDax[q.id] ? "Hide DAX" : "Show / Edit DAX"}
            </button>
            <button
              className="ob-gen-dax-btn"
              disabled={genDaxBusy[q.id] || !q.question.trim()}
              onClick={() => handleGenDax(setter, q)}
            >
              {genDaxBusy[q.id] ? <><span className="ob-spin" />Regenerating…</> : (q.verifyStatus === "failed" || q.verifyStatus === "error" || q.verifyError) ? "Regenerate DAX" : "Generate DAX"}
            </button>
          </div>
          {showDax[q.id] && (
            <div className="ob-dax">
              <textarea
                className="ob-textarea"
                rows={4}
                value={editDax[q.id] ?? q.dax}
                placeholder="DAX query…"
                onChange={(e) => {
                  setEditDax((s) => ({ ...s, [q.id]: e.target.value }));
                  setItem(setter, q.id, { dax: e.target.value, verifyStatus: "unverified" });
                }}
              />
              <SampleTable rows={q.sample} />
            </div>
          )}
          <div className="ob-item-actions">
            <div className="ob-rate">
              <button className={`up ${q.rating === 1 ? "on" : ""}`} onClick={() => setItem(setter, q.id, { rating: q.rating === 1 ? 0 : 1 })}>👍</button>
              <button className={`down ${q.rating === -1 ? "on" : ""}`} onClick={() => setItem(setter, q.id, { rating: q.rating === -1 ? 0 : -1 })}>👎</button>
            </div>
            <input className="ob-input ob-fb" placeholder="Feedback (optional)" value={q.feedback}
              onChange={(e) => setItem(setter, q.id, { feedback: e.target.value })} />
          </div>
        </div>
      ))}
      <button className="ob-add-btn" onClick={() => addQuestion(setter, scope)}>+ Add question</button>
    </div>
  );

  // ── Accordion panel renderers ──────────────────────────────────────────────
  const renderMeasuresPanel = () => {
    const emptyDaxCount = measures.filter((m) => !m.dax.trim() && m.name.trim()).length;
    return (
      <>
        <p className="ob-sub">Extracted from the semantic model. Confirm, edit DAX, annotate business meaning, or add your own.</p>
        {emptyDaxCount > 0 && measures.length > 0 && (
          <div style={{ textAlign: "right", marginBottom: 8 }}>
            <button className="ob-gen-dax-btn" disabled={genAllMeasuresBusy} onClick={handleGenAllMeasuresDax}>
              {genAllMeasuresBusy ? <><span className="ob-spin" />Generating DAX…</> : "Generate DAX for all"}
            </button>
          </div>
        )}
        <div className="ob-items">
          <div className="ob-select-all-bar">
            <button className="ob-toggle-link" onClick={() => setMeasures((list) => list.map((m) => ({ ...m, confirmed: !list.every((x) => x.confirmed) })))}>
              {measures.every((m) => m.confirmed) ? "Deselect all" : "Select all"}
            </button>
            <span className="ob-sel-count">{measures.filter((m) => m.confirmed).length} / {measures.length} confirmed</span>
          </div>
          {!measures.length && <div className="ob-empty">No measures found in this model.</div>}
          {measures.map((m) => (
            <div key={m.id} className={`ob-item ${m.confirmed ? "sel" : ""}`}>
              <div className="ob-item-head">
                <input type="checkbox" checked={m.confirmed} onChange={(e) => setItem(setMeasures, m.id, { confirmed: e.target.checked })} />
                <input className="ob-input" style={{ flex: 1, marginLeft: 8, fontWeight: 600 }}
                  value={m.name} placeholder="Measure name"
                  onChange={(e) => setItem(setMeasures, m.id, { name: e.target.value })} />
                {m.table && <span style={{ fontSize: 11.5, color: "#94a3b8", marginLeft: 6 }}>{m.table}</span>}
                <button className="ob-icon-btn ob-del" title="Remove" onClick={() => removeItem(setMeasures, m.id)}>✕</button>
              </div>
              <div className="ob-dax" style={{ marginTop: 8, position: "relative" }}>
                <textarea className="ob-textarea" rows={3}
                  value={m.dax} placeholder="DAX expression…"
                  onChange={(e) => setItem(setMeasures, m.id, { dax: e.target.value, verifyStatus: "unverified", verifyError: null })} />
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
                  {!m.dax.trim() ? (
                    <button className="ob-gen-dax-btn" disabled={genMeasureBusy[m.id] || !m.name.trim()} onClick={() => handleGenMeasureDax(m)}>
                      {genMeasureBusy[m.id] ? <><span className="ob-spin" />Generating…</> : "Generate DAX"}
                    </button>
                  ) : m.verifyStatus === "failed" ? (
                    <>
                      <button className="ob-gen-dax-btn" disabled={genMeasureBusy[m.id] || !m.name.trim()} onClick={() => handleGenMeasureDax(m)}>
                        {genMeasureBusy[m.id] ? <><span className="ob-spin" />Regenerating…</> : "Regenerate DAX"}
                      </button>
                      <button className="ob-gen-dax-btn" disabled={verifyMeasureBusy[m.id]} onClick={() => handleVerifyMeasure(m)}>
                        {verifyMeasureBusy[m.id] ? <><span className="ob-spin" />Verifying…</> : "Re-verify"}
                      </button>
                      <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }} title={m.verifyError || ""}>
                        Failed{m.verifyError ? `: ${m.verifyError}` : ""}
                      </span>
                    </>
                  ) : (
                    <>
                      <button className="ob-gen-dax-btn" disabled={verifyMeasureBusy[m.id]} onClick={() => handleVerifyMeasure(m)}>
                        {verifyMeasureBusy[m.id] ? <><span className="ob-spin" />Validating…</> : "Validate"}
                      </button>
                      {(m.verifyStatus === "ok" || m.verifyStatus === "valid") && (
                        <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>✓ Valid</span>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="ob-item-actions">
                <input className="ob-input ob-fb" placeholder="Business description" value={m.description}
                  onChange={(e) => setItem(setMeasures, m.id, { description: e.target.value })} />
              </div>
            </div>
          ))}
          <button className="ob-add-btn" onClick={addMeasure}>+ Add measure</button>
        </div>
      </>
    );
  };

  const renderQuestionsPanel = () => (
    <>
      <p className="ob-sub">AI-drafted questions verified against your dataset. Dashboard and model-scoped questions shown together.</p>

      <h6 style={{ margin: "16px 0 8px", fontSize: 13, fontWeight: 700, color: "#475569" }}>Dashboard Questions</h6>
      {renderQuestions(dashQ, setDashQ, "dashboard")}
      <div style={{ marginTop: 8 }}>
        <button className="ob-toggle-link" onClick={regenerateDashQ} disabled={!!ob.busy}>Regenerate dashboard questions</button>
      </div>

      <h6 style={{ margin: "20px 0 8px", fontSize: 13, fontWeight: 700, color: "#475569" }}>Model Questions</h6>
      <p className="ob-sub" style={{ marginBottom: 8 }}>Scoped to: {selectedTables.join(", ") || "your selected tables"}</p>
      {renderQuestions(srcQ, setSrcQ, "source_table")}
      <div style={{ marginTop: 8 }}>
        <button className="ob-toggle-link" onClick={regenerateSrcQ} disabled={!!ob.busy}>Regenerate model questions</button>
      </div>
    </>
  );

  const renderKpisPanel = () => (
    <>
      <p className="ob-sub">Inferred from the selected tables. Edit, add your own, rate and refine.</p>
      <div className="ob-items">
        <SelectAllBar list={kpis} setter={setKpis} />
        {!kpis.length && sectionStatus.kpis === "loading" && (
          <div className="ob-empty"><span className="ob-spin" /> Generating KPIs…</div>
        )}
        {!kpis.length && sectionStatus.kpis !== "loading" && (
          <div className="ob-empty">No KPIs yet. Add one below or click Regenerate.</div>
        )}
        {kpis.map((k) => (
          <div key={k.id} className={`ob-item ${k.selected ? "sel" : ""}`}>
            <div className="ob-item-head">
              <input type="checkbox" checked={k.selected} onChange={(e) => setItem(setKpis, k.id, { selected: e.target.checked })} />
              <input className="ob-input" style={{ flex: 1, marginLeft: 8, fontWeight: 600 }}
                value={k.name} placeholder="KPI name"
                onChange={(e) => setItem(setKpis, k.id, { name: e.target.value })} />
              {k.table && <span style={{ fontSize: 11.5, color: "#94a3b8", marginLeft: 6 }}>{k.table}</span>}
              <button className="ob-icon-btn ob-del" title="Remove" onClick={() => removeItem(setKpis, k.id)}>✕</button>
            </div>
            <textarea className="ob-textarea" rows={2} style={{ marginTop: 8 }}
              value={k.definition} placeholder="Definition / business meaning…"
              onChange={(e) => setItem(setKpis, k.id, { definition: e.target.value })} />
            <div className="ob-dax" style={{ marginTop: 6 }}>
              <textarea className="ob-textarea" rows={3}
                value={k.dax} placeholder="DAX / SQL (optional)…"
                onChange={(e) => setItem(setKpis, k.id, { dax: e.target.value })} />
            </div>
            <div className="ob-item-actions">
              <div className="ob-rate">
                <button className={`up ${k.rating === 1 ? "on" : ""}`} onClick={() => setItem(setKpis, k.id, { rating: k.rating === 1 ? 0 : 1 })}>👍</button>
                <button className={`down ${k.rating === -1 ? "on" : ""}`} onClick={() => setItem(setKpis, k.id, { rating: k.rating === -1 ? 0 : -1 })}>👎</button>
              </div>
              <input className="ob-input ob-fb" placeholder="Feedback (optional)" value={k.feedback}
                onChange={(e) => setItem(setKpis, k.id, { feedback: e.target.value })} />
            </div>
          </div>
        ))}
        <button className="ob-add-btn" onClick={addKpi}>+ Add KPI</button>
      </div>
      <div style={{ marginTop: 8 }}>
        <button className="ob-toggle-link" onClick={regenerateKpis} disabled={!!ob.busy}>Regenerate KPIs</button>
      </div>
    </>
  );

  const renderMetadataPanel = () => (
    <>
      <p className="ob-sub">Business descriptions and synonyms. Edit to match your terminology.</p>
      <div className="ob-items">
        <SelectAllBar list={metadata} setter={setMetadata} />
        {!metadata.length && sectionStatus.metadata === "loading" && (
          <div className="ob-empty"><span className="ob-spin" /> Generating metadata…</div>
        )}
        {!metadata.length && sectionStatus.metadata !== "loading" && (
          <div className="ob-empty">No metadata yet. Add a term below or click Regenerate.</div>
        )}
        {metadata.map((d) => (
          <div key={d.id} className={`ob-item ${d.selected ? "sel" : ""}`}>
            <div className="ob-item-head">
              <input type="checkbox" checked={d.selected} onChange={(e) => setItem(setMetadata, d.id, { selected: e.target.checked })} />
              <input className="ob-input" style={{ flex: 1, marginLeft: 8, fontWeight: 600 }}
                value={d.objectName} placeholder="Name (table / column / term)"
                onChange={(e) => setItem(setMetadata, d.id, { objectName: e.target.value })} />
              <select className="ob-select" style={{ minWidth: 100, marginLeft: 6 }}
                value={d.objectType}
                onChange={(e) => setItem(setMetadata, d.id, { objectType: e.target.value })}>
                <option value="term">Term</option>
                <option value="table">Table</option>
                <option value="column">Column</option>
              </select>
              <button className="ob-icon-btn ob-del" title="Remove" onClick={() => removeItem(setMetadata, d.id)}>✕</button>
            </div>
            <textarea className="ob-textarea" rows={2} style={{ marginTop: 8 }}
              value={d.description} placeholder="Business description…"
              onChange={(e) => setItem(setMetadata, d.id, { description: e.target.value })} />
            <input className="ob-input" style={{ marginTop: 6, width: "100%" }}
              value={d.synonyms} placeholder="Synonyms (comma-separated)…"
              onChange={(e) => setItem(setMetadata, d.id, { synonyms: e.target.value })} />
            <div className="ob-item-actions">
              <input className="ob-input ob-fb" placeholder="Feedback (optional)" value={d.feedback}
                onChange={(e) => setItem(setMetadata, d.id, { feedback: e.target.value })} />
            </div>
          </div>
        ))}
        <button className="ob-add-btn" onClick={addMeta}>+ Add term / definition</button>
      </div>
      <div style={{ marginTop: 8 }}>
        <button className="ob-toggle-link" onClick={regenerateMeta} disabled={!!ob.busy}>Regenerate metadata</button>
      </div>
    </>
  );

  const renderBusinessContextPanel = () => (
    <>
      <p className="ob-sub">Domain knowledge and business rules that help the DAX engine write accurate queries.</p>
      <div style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "center" }}>
        <button className="ob-gen-dax-btn" disabled={genContextBusy} onClick={handleGenDomainContext}>
          {genContextBusy ? <><span className="ob-spin" />Generating…</> : domainContext ? "Regenerate" : "Generate Business Context"}
        </button>
        {domainContext && <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>Context ready</span>}
      </div>
      <textarea
        className="ob-textarea"
        rows={12}
        value={domainContext}
        placeholder={"Domain-specific context (AI will generate or write your own)…"}
        onChange={(e) => setDomainContext(e.target.value)}
        style={{ width: "100%", fontFamily: "monospace", fontSize: 13, lineHeight: 1.6 }}
      />
    </>
  );

  const renderReviewPanel = () => {
    const selDash = dashQ.filter((q) => q.selected).length;
    const selSrc = srcQ.filter((q) => q.selected).length;
    const selKpi = kpis.filter((k) => k.selected).length;
    const selMeta = metadata.filter((d) => d.selected).length;
    const confMeas = measures.filter((m) => m.confirmed).length;
    const verifiedMeas = measures.filter((m) => m.verifyStatus === "ok" || m.verifyStatus === "valid").length;
    return (
      <>
        <p className="ob-sub">This grounding will be saved for <b>{selectedReport?.reportName}</b> and used by the Text-to-DAX query engine.</p>
        <div className="ob-stat-row">
          <div className="ob-stat"><div className="n">{selectedTables.length}</div><div className="l">Semantic Tables</div></div>
          <div className="ob-stat"><div className="n">{confMeas}</div><div className="l">Measures Confirmed</div></div>
          <div className="ob-stat"><div className="n">{verifiedMeas}</div><div className="l">Measures Verified</div></div>
        </div>
        <div className="ob-stat-row">
          <div className="ob-stat"><div className="n">{selDash + selSrc}</div><div className="l">Q&A Pairs</div></div>
          <div className="ob-stat"><div className="n">{selKpi}</div><div className="l">KPIs</div></div>
          <div className="ob-stat"><div className="n">{selMeta}</div><div className="l">Metadata</div></div>
          {domainContext.trim() && <div className="ob-stat"><div className="n">✓</div><div className="l">Business Context</div></div>}
        </div>
      </>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const panels = [
    { key: "measures", title: "Semantic Model & Measures", render: renderMeasuresPanel },
    { key: "questions", title: "Sample Questions", render: renderQuestionsPanel },
    { key: "kpis", title: "KPIs", render: renderKpisPanel },
    { key: "metadata", title: "Metadata & Definitions", render: renderMetadataPanel },
    { key: "context", title: "Business Context", render: renderBusinessContextPanel },
    { key: "review", title: "Review & Finish", render: renderReviewPanel },
  ];

  return (
    <div className="ob-wrap">
      {ob.error && <div className="ob-alert err">{ob.error}</div>}
      {notice && <div className="ob-alert info">{notice}</div>}

      {phase === "connect" && (
        <div className="ob-panel">
          <h5>Choose a dashboard to onboard</h5>
          <p className="ob-sub">Pick a registered Power BI report. We'll read its semantic model — tables, columns and measure DAX — directly from the dataset.</p>
          <div className="ob-picker">
            <div>
              <label htmlFor="ob-report">Registered report</label>
              <select id="ob-report" className="ob-select" value={reportGuid} onChange={(e) => { setReportGuid(e.target.value); setExistingOnboarding(null); setNotice(null); }}>
                <option value="">— select —</option>
                {reports.map((r) => <option key={r.reportId} value={r.reportId}>{r.reportName} ({r.reportId})</option>)}
              </select>
            </div>
            <AppButton variant="fill" disabled={!reportGuid || ob.busy === "start" || ob.busy === "state" || ob.busy === "statusByGuid"} onClick={handleStart}>
              {(ob.busy === "start" || ob.busy === "state" || ob.busy === "statusByGuid") ? <><span className="ob-spin" />Checking…</> : "Connect & introspect"}
            </AppButton>
          </div>
          {selectedReport && (
            <p className="ob-sub" style={{ marginTop: 12 }}>
              Workspace/group <b>{selectedReport.groupId}</b> · dataset <b>{selectedReport.datasetId || "—"}</b>
            </p>
          )}

          {existingOnboarding && (
            <div className="ob-existing" style={{ marginTop: 20, padding: 16, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#166534", marginBottom: 8 }}>This dashboard already has AI onboarding</div>
              <p style={{ fontSize: 13, color: "#15803d", marginBottom: 12 }}>
                Status: <b>{existingOnboarding.status}</b> · Onboarding ID: {existingOnboarding.onboarding_id}
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                <AppButton variant="fill" onClick={() => {
                  setOnboardingId(existingOnboarding.onboarding_id);
                  setPhase("done");
                }} disabled={!!ob.busy}>
                  Open Chatbot
                </AppButton>
                <AppButton variant="secondary" onClick={handleEdit} disabled={!!ob.busy}>
                  {ob.busy === "reopen" ? <><span className="ob-spin" />Reopening…</> : "Edit Onboarding"}
                </AppButton>
                <AppButton variant="danger" onClick={() => setShowDeleteConfirm(true)} disabled={!!ob.busy}>
                  Delete Onboarding
                </AppButton>
              </div>
              {showDeleteConfirm && (
                <div style={{ marginTop: 12, padding: 12, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}>
                  <p style={{ fontSize: 13, color: "#991b1b", fontWeight: 600, marginBottom: 8 }}>
                    Are you sure? This will permanently delete the onboarding and disable the chatbot for this dashboard.
                  </p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <AppButton variant="fill" style={{ background: "#dc2626" }} onClick={handleDelete} disabled={ob.busy === "delete"}>
                      {ob.busy === "delete" ? <><span className="ob-spin" />Deleting…</> : "Yes, delete"}
                    </AppButton>
                    <AppButton variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</AppButton>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {phase === "tables" && (
        <div className="ob-panel">
          <h5>Select semantic model tables</h5>
          <p className="ob-sub">{Object.keys(tables).length} tables discovered. Selected tables scope the questions, KPIs and metadata generated next.</p>
          <div className="ob-select-all-bar">
            <button className="ob-toggle-link" onClick={() => setSelectedTables(Object.keys(tables).length === selectedTables.length ? [] : Object.keys(tables))}>
              {selectedTables.length === Object.keys(tables).length ? "Deselect all" : "Select all"}
            </button>
            <span className="ob-sel-count">{selectedTables.length} / {Object.keys(tables).length} selected</span>
          </div>
          <div className="ob-tablegrid">
            {Object.keys(tables).map((t) => (
              <div key={t} className={`ob-tablecard ${selectedTables.includes(t) ? "sel" : ""}`} onClick={() => toggleTable(t)}>
                <div className="ob-tname">{t}<span className="ob-check">{selectedTables.includes(t) ? "✓" : ""}</span></div>
                <div className="ob-tcols">{(tables[t] || []).join(", ") || "—"}</div>
              </div>
            ))}
            {!Object.keys(tables).length && <div className="ob-empty">No tables discovered.</div>}
          </div>
          <div className="ob-footer" style={{ marginTop: 16 }}>
            <AppButton variant="secondary" onClick={() => setPhase("connect")}>Back</AppButton>
            <AppButton variant="fill" disabled={!selectedTables.length || !!ob.busy} onClick={saveTablesAndPrepopulate}>
              {ob.busy === "prepopulate" ? <><span className="ob-spin" />Generating all sections…</> : "Confirm tables & generate"}
            </AppButton>
          </div>
        </div>
      )}

      {phase === "accordion" && (
        <div className="ob-panel">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div>
              <h5 style={{ margin: 0 }}>AI Onboarding — {selectedReport?.reportName || "Dashboard"}</h5>
              <p className="ob-sub" style={{ margin: "4px 0 0" }}>{selectedTables.length} tables selected · All sections editable independently</p>
            </div>
            <AppButton variant="secondary" onClick={() => setPhase("tables")}>Back to tables</AppButton>
          </div>

          {panels.map(({ key, title, render }) => (
            <details
              key={key}
              className="ob-accordion"
              open={expandedPanels.has(key)}
              onToggle={(e) => {
                const isOpen = e.target.open;
                setExpandedPanels((s) => {
                  const n = new Set(s);
                  isOpen ? n.add(key) : n.delete(key);
                  return n;
                });
              }}
            >
              <summary className="ob-accordion-summary">
                <span className="ob-accordion-title">{title}</span>
                <PanelStatus status={sectionStatus[key]} />
              </summary>
              <div className="ob-accordion-content">
                {render()}
              </div>
            </details>
          ))}

          <div className="ob-footer" style={{ marginTop: 20 }}>
            <AppButton variant="fill" disabled={!!ob.busy} onClick={doFinalize}>
              {ob.busy === "finalize" || ob.busy === "feedback" || ob.busy === "domainContext" ? <><span className="ob-spin" />Saving &amp; finalizing…</> : "Save all & enable chatbot"}
            </AppButton>
          </div>
        </div>
      )}

      {phase === "done" && (
        <div className="ob-panel">
          <div className="ob-done">
            <div className="ob-tick">✓</div>
            <h5>Chatbot enabled for {selectedReport?.reportName || "this dashboard"}</h5>
            <p className="ob-sub">Users can now ask questions in natural language against this dashboard. Try it out below!</p>
          </div>
          <div style={{ marginTop: 16 }}>
            <OnboardingChat
              reportGuid={reportGuid}
              reportName={selectedReport?.reportName || "Dashboard"}
              suggestedQuestions={[
                ...dashQ.filter(q => q.selected && q.question).map(q => q.question),
                ...srcQ.filter(q => q.selected && q.question).map(q => q.question),
              ].slice(0, 6)}
            />
          </div>
          <div style={{ marginTop: 18, display: "flex", gap: 10, justifyContent: "center" }}>
            <AppButton variant="fill" onClick={() => {
              autoGenRef.current = false;
              autoValidatedRef.current = new Set();
              setPhase("accordion");
            }}>Edit Onboarding</AppButton>
            <AppButton variant="secondary" onClick={() => {
              setPhase("connect"); setReportGuid(""); setOnboardingId(null);
              setDashQ([]); setSrcQ([]); setKpis([]); setMetadata([]);
              setSelectedTables([]); setMeasures([]); setDomainContext("");
              setExistingOnboarding(null); autoGenRef.current = false; autoValidatedRef.current = new Set();
            }}>Onboard another dashboard</AppButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingWizard;
